import {
  FormkitForm,
  FormkitNode,
  FormkitSection,
  hasLiquid,
  NodeType,
} from "@hightouch/formkit";
import { formkitRenderer } from "@hightouch/lib/templating/formkit-renderer";
import { get } from "lodash";

import { OverrideConfig } from "src/components/destinations/types";
import { useFormkitContext } from "./components/formkit-context";
import { useQuery } from "./hooks";
import { JourneyIcon, WarningIcon } from "@hightouch/htui";

export type SpecialQuery = {
  enabled: boolean;
  data: any;
  error: unknown;
  isFetching: boolean;
  refetch?: () => void;
};

export function specialQuery(name: string, options?: any): SpecialQuery {
  const enabled =
    !Array.isArray(options) && options !== null && options !== undefined;
  // TODO: Fix these hook issues.
  // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  const { destination, model } = useFormkitContext();
  // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  const { data, error, refetch, isFetching } = useQuery<any>(
    JSON.stringify({ name, variables: options?.variables }),
    {
      enabled: enabled,
      fetchProps: {
        destinationId: destination?.id,
        modelId: model?.id,
        query: options?.query,
        variables: options?.variables,
      },
      keepPreviousData: true,
    },
  );
  return {
    enabled,
    data,
    error,
    refetch: enabled ? refetch : undefined,
    isFetching,
  };
}

export function parseHeadings(
  node: FormkitSection,
  context: Record<string, unknown>,
): { heading: string; subheading: string } {
  const engine =
    hasLiquid(node.heading) || hasLiquid(node.subheading)
      ? formkitRenderer
      : null;
  const heading =
    node.heading && engine
      ? engine.parseAndRenderSync(node.heading, context)
      : node.heading
        ? node.heading
        : "";
  const subheading =
    node.subheading && engine
      ? engine.parseAndRenderSync(node.subheading, context)
      : node.subheading
        ? node.subheading
        : "";
  return { heading, subheading };
}

/**
 * Show card if this is a sync attached to a template and
 * 1. The field is unlocked or
 * 2. At least one child is unlocked
 */
export function hasVisibleChild(
  node: FormkitNode,
  overrideConfig: OverrideConfig | null | undefined,
) {
  if (!overrideConfig) {
    return true;
  }

  if (node.type === NodeType.Component) {
    return Boolean(get(overrideConfig, node.key)?.overridable);
  }

  return node.children.some((child) => hasVisibleChild(child, overrideConfig));
}

export const shouldWrapInSection = (node: FormkitForm) => {
  return node.children?.some((node) => {
    if (node.type === "layout" && node.layout === "section") {
      return node.size === "small";
    }
    if (node.type === "component") {
      return node.component !== "message";
    }
    return false;
  });
};

export function getIconComponent(iconName: string) {
  switch (iconName) {
    case "Journey":
      return JourneyIcon;
    case "Warning":
      return WarningIcon;
    default:
      return undefined;
  }
}
